import { useState, useCallback, useMemo } from "react";
import { Plus, FileText, Lock, CheckCircle, Clock, XCircle, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BidSheetEditor } from "../estimating";
import { formatCurrency } from "./utils";

/**
 * EstimatingTab - Manages multiple bid sheets (Base Bid, Alternates, Change Orders)
 */
const EstimatingTab = ({
  job,
  materials,
  equipment,
  employeeRoles,
  miscItemsFromSettings,
  miscUnits = [],
  defaultBurdenPercent,
  defaultSalesTaxPercent,
  canManageJobs,
  userRole,
  onSaveBidSheet,
  onApproveCO,      // Backend endpoint for approving CO
  onRevokeCOApproval, // Backend endpoint for revoking approval
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addDialogType, setAddDialogType] = useState("alternate"); // "alternate" or "change_order"
  const [newBidDescription, setNewBidDescription] = useState("");
  const [copyMode, setCopyMode] = useState(false); // false = new, true = copy
  const [copySourceId, setCopySourceId] = useState("");
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalDate, setApprovalDate] = useState("");
  const [selectedCOForApproval, setSelectedCOForApproval] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bidSheetToDelete, setBidSheetToDelete] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  
  // Initialize selected bid sheet ID from job data
  const [selectedBidSheetId, setSelectedBidSheetId] = useState(() => {
    if ((job.bid_sheets || []).length > 0) {
      const bs = job.bid_sheets;
      const base = bs.find(b => b.bid_type === "base_bid" || (!b.bid_type && !b.is_change_order));
      return base?.id || bs[0]?.id;
    }
    return "new_base_bid";
  });

  // Determine if contract has been received
  // Bidding archive statuses (not_bid, bid_complete, not_won) keep bid sheets unlocked
  const biddingArchiveStatuses = ["not_bid", "bid_complete", "not_won"];
  const isBiddingArchiveStatus = biddingArchiveStatuses.includes(job.status);
  const hasContract = job.contract_received_date && job.status !== "bidding" && !isBiddingArchiveStatus;
  const isBidding = job.status === "bidding" || isBiddingArchiveStatus;

  // Get bid sheets by type - memoized to prevent dependency issues
  const bidSheets = useMemo(() => job.bid_sheets || [], [job.bid_sheets]);
  const baseBid = useMemo(() => 
    bidSheets.find(bs => bs.bid_type === "base_bid" || (!bs.bid_type && !bs.is_change_order)),
    [bidSheets]
  );
  const alternates = useMemo(() => 
    bidSheets.filter(bs => bs.bid_type === "alternate"),
    [bidSheets]
  );
  const changeOrders = useMemo(() => 
    bidSheets.filter(bs => bs.bid_type === "change_order" || bs.is_change_order),
    [bidSheets]
  );

  // Compute effective selected ID - automatically falls back to valid ID
  const effectiveSelectedId = useMemo(() => {
    if (bidSheets.length > 0) {
      // Check if current selection is valid
      const isValidSelection = bidSheets.some(bs => bs.id === selectedBidSheetId);
      if (isValidSelection) return selectedBidSheetId;
      // Fall back to base bid or first bid sheet
      return baseBid?.id || bidSheets[0]?.id;
    }
    return "new_base_bid";
  }, [bidSheets, baseBid, selectedBidSheetId]);

  const getDefaultBidSheet = useCallback(() => ({
    id: crypto.randomUUID(),
    name: "Base Bid",
    description: "",
    bid_type: "base_bid",
    material_items: [],
    equipment_items: [],
    labor_entries: [],
    misc_items: [],
    burden_percent: defaultBurdenPercent || 28,
    sales_tax_percent: defaultSalesTaxPercent || 8.25,
    profit_percent: 25,
    tax_exempt: false,
    scaffold_sqft: 0,
    estimated_weeks: 0,
    is_locked: false,
    is_accepted: false,
    co_status: "pending",
  }), [defaultBurdenPercent, defaultSalesTaxPercent]);

  const selectedBidSheet = useMemo(() => 
    bidSheets.find(bs => bs.id === effectiveSelectedId) || 
    (effectiveSelectedId === "new_base_bid" ? getDefaultBidSheet() : null),
    [bidSheets, effectiveSelectedId, getDefaultBidSheet]
  );

  // Handle adding new alternate or change order
  const handleAddBidSheet = useCallback(() => {
    const isChangeOrder = addDialogType === "change_order";
    const existingOfType = isChangeOrder ? changeOrders : alternates;
    const number = existingOfType.length + 1;
    
    // If copying, deep-clone the source sheet's data
    const sourceSheet = copyMode && copySourceId ? bidSheets.find(bs => bs.id === copySourceId) : null;
    
    const newBidSheet = {
      id: crypto.randomUUID(),
      name: isChangeOrder ? `Change Order #${number}` : `Alternate ${number}`,
      description: newBidDescription || (sourceSheet?.description || ""),
      bid_type: isChangeOrder ? "change_order" : "alternate",
      burden_percent: sourceSheet?.burden_percent ?? baseBid?.burden_percent ?? defaultBurdenPercent ?? 28,
      sales_tax_percent: sourceSheet?.sales_tax_percent ?? baseBid?.sales_tax_percent ?? defaultSalesTaxPercent ?? 8.25,
      profit_percent: sourceSheet?.profit_percent ?? baseBid?.profit_percent ?? 25,
      tax_exempt: sourceSheet?.tax_exempt ?? baseBid?.tax_exempt ?? false,
      scaffold_sqft: sourceSheet?.scaffold_sqft ?? 0,
      estimated_weeks: sourceSheet?.estimated_weeks ?? 0,
      is_locked: false,
      is_accepted: false,
      is_change_order: isChangeOrder,
      co_status: isChangeOrder ? "pending" : undefined,
      // Copy line items from source or start empty
      material_items: sourceSheet ? JSON.parse(JSON.stringify(sourceSheet.material_items || [])) : [],
      equipment_items: sourceSheet ? JSON.parse(JSON.stringify(sourceSheet.equipment_items || [])) : [],
      labor_entries: sourceSheet ? JSON.parse(JSON.stringify(sourceSheet.labor_entries || [])) : [],
      misc_items: sourceSheet ? JSON.parse(JSON.stringify(sourceSheet.misc_items || [])) : [],
    };

    const updatedBidSheets = [...bidSheets, newBidSheet];
    onSaveBidSheet(updatedBidSheets);
    setSelectedBidSheetId(newBidSheet.id);
    setAddDialogOpen(false);
    setNewBidDescription("");
    setCopyMode(false);
    setCopySourceId("");
  }, [addDialogType, newBidDescription, copyMode, copySourceId, alternates, changeOrders, baseBid, bidSheets, defaultBurdenPercent, defaultSalesTaxPercent, onSaveBidSheet]);

  // Handle saving a specific bid sheet
  const handleSaveSingleBidSheet = useCallback(async (updatedBidSheets) => {
    // updatedBidSheets is an array with the single updated bid sheet
    const updatedSheet = updatedBidSheets[0];
    
    // Merge with existing bid sheets
    const newBidSheets = bidSheets.map(bs => 
      bs.id === updatedSheet.id ? updatedSheet : bs
    );
    
    // If it's a new bid sheet, add it
    if (!bidSheets.find(bs => bs.id === updatedSheet.id)) {
      newBidSheets.push(updatedSheet);
    }
    
    return onSaveBidSheet(newBidSheets);
  }, [bidSheets, onSaveBidSheet]);

  // Handle approving a change order - now uses backend endpoint
  const handleApproveChangeOrder = useCallback(async () => {
    if (!selectedCOForApproval || !approvalDate || !onApproveCO) return;
    
    setIsApproving(true);
    try {
      await onApproveCO(selectedCOForApproval.id, approvalDate);
      setApprovalDialogOpen(false);
      setApprovalDate("");
      setSelectedCOForApproval(null);
    } finally {
      setIsApproving(false);
    }
  }, [selectedCOForApproval, approvalDate, onApproveCO]);

  // Handle revoking CO approval - now uses backend endpoint
  const handleUnapproveChangeOrder = useCallback(async (coSheet) => {
    if (!onRevokeCOApproval) return;
    await onRevokeCOApproval(coSheet.id);
  }, [onRevokeCOApproval]);

  // Handle deleting a bid sheet (alternates and change orders only)
  const handleDeleteBidSheet = useCallback(() => {
    if (!bidSheetToDelete) return;
    
    const updatedBidSheets = bidSheets.filter(bs => bs.id !== bidSheetToDelete.id);
    onSaveBidSheet(updatedBidSheets);
    
    // If the deleted sheet was selected, switch to base bid
    if (selectedBidSheetId === bidSheetToDelete.id) {
      const base = updatedBidSheets.find(b => b.bid_type === "base_bid");
      setSelectedBidSheetId(base?.id || updatedBidSheets[0]?.id);
    }
    
    setDeleteDialogOpen(false);
    setBidSheetToDelete(null);
  }, [bidSheetToDelete, bidSheets, selectedBidSheetId, onSaveBidSheet]);

  // Open delete confirmation
  const openDeleteDialog = (bidSheet) => {
    setBidSheetToDelete(bidSheet);
    setDeleteDialogOpen(true);
  };

  // Check if user can manage CO approvals (admin or project_manager only)
  const canManageApprovals = userRole === "admin" || userRole === "project_manager";

  // Calculate totals
  const acceptedTotal = bidSheets
    .filter(bs => bs.is_accepted)
    .reduce((sum, bs) => sum + (bs.grand_total || 0), 0);
  
  const approvedCOTotal = changeOrders
    .filter(co => co.co_status === "approved")
    .reduce((sum, co) => sum + (co.grand_total || 0), 0);

  const getStatusIcon = (bidSheet) => {
    if (bidSheet.is_locked) return <Lock className="w-4 h-4 text-muted-foreground" />;
    if (bidSheet.is_accepted) return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (bidSheet.co_status === "approved") return <CheckCircle className="w-4 h-4 text-green-600" />;
    if (bidSheet.co_status === "pending") return <Clock className="w-4 h-4 text-amber-500" />;
    if (bidSheet.co_status === "rejected") return <XCircle className="w-4 h-4 text-red-500" />;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Bid Sheet Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Bid Worksheets
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Add Alternate - when no contract yet */}
              {!hasContract && canManageJobs && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddDialogType("alternate");
                    setCopyMode(false);
                    setCopySourceId("");
                    setAddDialogOpen(true);
                  }}
                  data-testid="add-alternate-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Alternate
                </Button>
              )}
              {/* Add Change Order - only after contract */}
              {hasContract && canManageJobs && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAddDialogType("change_order");
                    setCopyMode(false);
                    setCopySourceId("");
                    setAddDialogOpen(true);
                  }}
                  data-testid="add-change-order-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Change Order
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {/* Base Bid */}
            {baseBid && (
              <BidSheetCard
                bidSheet={{
                  ...baseBid,
                  description: baseBid.description || job.description
                }}
                isSelected={effectiveSelectedId === baseBid.id}
                onClick={() => setSelectedBidSheetId(baseBid.id)}
                statusIcon={getStatusIcon(baseBid)}
                hasContract={hasContract}
              />
            )}
            {!baseBid && effectiveSelectedId === "new_base_bid" && (
              <BidSheetCard
                bidSheet={{ name: "Base Bid", grand_total: 0, bid_type: "base_bid", description: job.description }}
                isSelected={true}
                onClick={() => {}}
                isNew
              />
            )}

            {/* Alternates */}
            {alternates.map(alt => (
              <BidSheetCard
                key={alt.id}
                bidSheet={alt}
                isSelected={effectiveSelectedId === alt.id}
                onClick={() => setSelectedBidSheetId(alt.id)}
                statusIcon={getStatusIcon(alt)}
                onDelete={canManageJobs && !alt.is_locked ? () => openDeleteDialog(alt) : null}
                hasContract={hasContract}
              />
            ))}

            {/* Change Orders */}
            {changeOrders.map(co => (
              <BidSheetCard
                key={co.id}
                bidSheet={co}
                isSelected={effectiveSelectedId === co.id}
                onClick={() => setSelectedBidSheetId(co.id)}
                statusIcon={getStatusIcon(co)}
                onApprove={co.co_status === "pending" && hasContract && canManageJobs ? () => {
                  setSelectedCOForApproval(co);
                  setApprovalDialogOpen(true);
                } : null}
                onUnapprove={co.co_status === "approved" && canManageApprovals ? () => handleUnapproveChangeOrder(co) : null}
                onDelete={canManageJobs && !co.is_locked && co.co_status !== "approved" ? () => openDeleteDialog(co) : null}
                hasContract={hasContract}
              />
            ))}
          </div>

          {/* Summary */}
          {(acceptedTotal > 0 || approvedCOTotal > 0) && (
            <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm">
              {acceptedTotal > 0 && (
                <div>
                  <span className="text-muted-foreground">Accepted Bid Total: </span>
                  <span className="font-semibold">{formatCurrency(acceptedTotal)}</span>
                </div>
              )}
              {approvedCOTotal > 0 && (
                <div>
                  <span className="text-muted-foreground">Approved COs: </span>
                  <span className="font-semibold">{formatCurrency(approvedCOTotal)}</span>
                </div>
              )}
              {(acceptedTotal > 0 || approvedCOTotal > 0) && (
                <div>
                  <span className="text-muted-foreground">Total Contract: </span>
                  <span className="font-semibold text-primary">{formatCurrency(acceptedTotal + approvedCOTotal)}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Bid Sheet Editor */}
      {selectedBidSheet && (
        <BidSheetEditor
          key={effectiveSelectedId}
          initialBidSheet={selectedBidSheet}
          materials={materials}
          equipment={equipment}
          employeeRoles={employeeRoles}
          miscItemsFromSettings={miscItemsFromSettings}
          miscUnits={miscUnits}
          defaultBurdenPercent={defaultBurdenPercent}
          defaultSalesTaxPercent={defaultSalesTaxPercent}
          canManageJobs={canManageJobs && !selectedBidSheet.is_locked}
          onSaveBidSheet={handleSaveSingleBidSheet}
          title={selectedBidSheet.name}
          subtitle={selectedBidSheet.description}
          isLocked={selectedBidSheet.is_locked}
          takeoffData={job.takeoff}
          changeOrders={job.change_orders || []}
          effectiveTotal={job.estimate_total}
        />
      )}

      {/* Add Alternate/Change Order Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {addDialogType === "alternate" ? "Add Alternate Bid" : "Add Change Order"}
            </DialogTitle>
            <DialogDescription>
              {addDialogType === "alternate" 
                ? "Create an alternate bid option to present to the client."
                : "Create a change order to track modifications to the contract. Use negative values for deductive change orders."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* New or Copy */}
            <div className="space-y-2">
              <Label>Create Method</Label>
              <Select value={copyMode ? "copy" : "new"} onValueChange={(v) => { setCopyMode(v === "copy"); setCopySourceId(""); }}>
                <SelectTrigger data-testid="create-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New Worksheet (blank)</SelectItem>
                  <SelectItem value="copy">Copy Existing Worksheet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source sheet selector (when copying) */}
            {copyMode && (
              <div className="space-y-2">
                <Label>Copy From</Label>
                <Select value={copySourceId} onValueChange={setCopySourceId}>
                  <SelectTrigger data-testid="copy-source-select">
                    <SelectValue placeholder="Select worksheet to copy..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...bidSheets].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((bs) => (
                      <SelectItem key={bs.id} value={bs.id}>
                        {bs.name} {bs.grand_total ? `(${formatCurrency(bs.grand_total)})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="bid-description">Description (optional)</Label>
              <Textarea
                id="bid-description"
                placeholder={addDialogType === "alternate" 
                  ? "e.g., Premium stone upgrade, Extended warranty..."
                  : "e.g., Additional brick work on east wall, Credit for removed scope..."}
                value={newBidDescription}
                onChange={(e) => setNewBidDescription(e.target.value)}
                data-testid="bid-description-input"
              />
            </div>
            {addDialogType === "change_order" && !copyMode && (
              <p className="text-sm text-muted-foreground bg-blue-50 p-3 rounded-lg">
                The Change Order bid sheet will start empty. Add materials, labor, and equipment line items as needed. Use negative quantities or amounts for deductions.
              </p>
            )}
            {copyMode && copySourceId && (
              <p className="text-sm text-muted-foreground bg-green-50 p-3 rounded-lg">
                All materials, labor, equipment, and misc items will be copied from the selected worksheet.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddBidSheet}
              data-testid="confirm-add-bid-btn"
              disabled={copyMode && !copySourceId}
            >
              {addDialogType === "alternate" ? "Add Alternate" : "Add Change Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Order Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Change Order</DialogTitle>
            <DialogDescription>
              Approving this change order will add {formatCurrency(selectedCOForApproval?.grand_total || 0)} to the contract total.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="approval-date">Approval Date</Label>
              <Input
                id="approval-date"
                type="date"
                value={approvalDate}
                onChange={(e) => setApprovalDate(e.target.value)}
                data-testid="approval-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialogOpen(false)} disabled={isApproving}>
              Cancel
            </Button>
            <Button 
              onClick={handleApproveChangeOrder}
              disabled={!approvalDate || isApproving}
              data-testid="confirm-approve-co-btn"
            >
              {isApproving ? "Approving..." : "Approve Change Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Bid Sheet Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bidSheetToDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this {bidSheetToDelete?.bid_type === "change_order" ? "change order bid sheet" : "alternate bid"} and all its line items. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBidSheetToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBidSheet}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-bid-sheet-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Bid Sheet Card Component
const BidSheetCard = ({ bidSheet, isSelected, onClick, statusIcon, isNew, onApprove, onUnapprove, onDelete, hasContract }) => {
  const isChangeOrder = bidSheet.bid_type === "change_order" || bidSheet.is_change_order;
  const isAlternate = bidSheet.bid_type === "alternate";
  const isContracted = hasContract && bidSheet.is_accepted && !isChangeOrder;
  
  return (
    <div
      className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
        isSelected 
          ? "border-primary bg-primary/5" 
          : isContracted
            ? "border-green-500 bg-green-50/30"
            : "border-border hover:border-primary/50"
      }`}
      onClick={onClick}
      data-testid={`bid-sheet-card-${bidSheet.id || "new"}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm truncate">{bidSheet.name}</span>
          {statusIcon}
        </div>
        <div className="flex items-center gap-1">
          {isContracted && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
              Contracted
            </span>
          )}
          {isChangeOrder && (
            <span className={`text-xs px-1.5 py-0.5 rounded ${
              bidSheet.co_status === "approved" ? "bg-green-100 text-green-700" :
              bidSheet.co_status === "rejected" ? "bg-red-100 text-red-700" :
              "bg-amber-100 text-amber-700"
            }`}>
              {bidSheet.co_status === "approved" ? "Approved" : 
               bidSheet.co_status === "rejected" ? "Rejected" : "Pending"}
            </span>
          )}
          {isAlternate && bidSheet.is_accepted && !hasContract && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">
              Accepted
            </span>
          )}
          {onDelete && (
            <button
              className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              data-testid={`delete-bid-sheet-btn-${bidSheet.id}`}
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {bidSheet.description && (
        <p className="text-xs text-muted-foreground truncate mb-1">{bidSheet.description}</p>
      )}
      <div className="text-lg font-semibold">
        {isNew ? "$0.00" : formatCurrency(bidSheet.grand_total || 0)}
      </div>
      {onApprove && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 w-full text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onApprove();
          }}
          data-testid={`approve-co-btn-${bidSheet.id}`}
        >
          <CheckCircle className="w-3 h-3 mr-1" />
          Approve
        </Button>
      )}
      {onUnapprove && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 w-full text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
          onClick={(e) => {
            e.stopPropagation();
            onUnapprove();
          }}
          data-testid={`unapprove-co-btn-${bidSheet.id}`}
        >
          <XCircle className="w-3 h-3 mr-1" />
          Revoke Approval
        </Button>
      )}
    </div>
  );
};

export default EstimatingTab;
